import _ from "lodash";
import { Url } from "next/dist/shared/lib/router/router";

import { Link, LinkProps } from "@chakra-ui/next-js";
import {
  As,
  Button,
  ButtonProps,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemProps,
  MenuList,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";

import { ChevronDownIcon, DeleteIcon, EditIcon, ExternalLinkIcon } from "@/components/shared/ReleaseIcons";

const defaultIcons = {
  edit: EditIcon,
  delete: DeleteIcon,
  externalLink: ExternalLinkIcon,
};

export function Actions({ actions }: { actions: ActionItem[] }) {
  const renderLabel = (item: ActionItem) => item.label || _.startCase(item.type);

  const renderIcon = (item: ActionItem) => {
    const iconAs = item.icon || defaultIcons[item.type as keyof typeof defaultIcons];
    return item.isLoading ? <Spinner size="xs" /> : iconAs && <Icon as={iconAs} h="4" w="4" />;
  };

  const renderMenuItem = (item: ActionItem, props: MenuItemProps = {}) => (
    <MenuItem key={item.label} {..._.omitBy({ icon: renderIcon(item), ...props }, _.isNil)}>
      {renderLabel(item)}
    </MenuItem>
  );

  const renderMenu = (item: ActionMenuType, primary: boolean) => {
    const primaryMenuProps = primary ? {} : { variant: "outline" };

    return (
      <Menu key={item.label}>
        <MenuButton
          as={Button}
          isDisabled={item.isLoading || item.isDisabled}
          rightIcon={item.isLoading ? <Spinner size="xs" /> : <ChevronDownIcon />}
          size="action"
          {...primaryMenuProps}
        >
          {renderLabel(item)}
        </MenuButton>
        <MenuList>
          {item.items.map(menuItem => {
            menuItem.label = renderLabel(menuItem);
            switch (menuItem.type) {
              case "link":
              case "externalLink":
              case "edit":
                return renderMenuItem(menuItem, {
                  isDisabled: !!menuItem.isDisabled,
                  ...(menuItem.isDisabled
                    ? {}
                    : {
                        as: Link,
                        href: menuItem.href,
                        target: menuItem.type === "externalLink" ? "_blank" : undefined,
                      }),
                });
              case "function":
              case "delete":
                return renderMenuItem(menuItem, {
                  isDisabled: !!menuItem.isDisabled,
                  onClick: menuItem.function,
                });
            }
          })}
        </MenuList>
      </Menu>
    );
  };

  const renderButton = (item: ActionItem, props: ButtonProps & LinkProps) => (
    <Tooltip key={item.label} label={item.tooltip}>
      <Button size="action" {..._.omitBy({ variant: item.variant, ...props }, _.isNil)}>
        {renderLabel(item)}
      </Button>
    </Tooltip>
  );

  return (
    <HStack spacing={2}>
      {actions.map(actionItem => {
        switch (actionItem.type) {
          case "menu":
            return renderMenu(actionItem, false);
          case "primaryMenu":
            return renderMenu(actionItem, true);
          case "link":
          case "externalLink":
          case "edit":
            return renderButton(actionItem, {
              isDisabled: !!actionItem.isDisabled,
              leftIcon: renderIcon(actionItem),
              target: actionItem.type === "externalLink" ? "_blank" : undefined,
              href: "#",
              ...(actionItem.isDisabled ? {} : { as: Link, href: actionItem.href }),
            });
          case "function":
          case "delete":
            return renderButton(actionItem, {
              href: "#",
              isDisabled: !!actionItem.isDisabled || actionItem.isLoading,
              leftIcon: renderIcon(actionItem),
              onClick: actionItem.function,
            });
        }
      })}
    </HStack>
  );
}

type ActionItemBase = {
  label?: string;
  icon?: As;
  variant?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  tooltip?: string;
};

export interface ActionMenuType extends ActionItemBase {
  type: "menu" | "primaryMenu";
  label: string;
  items: Array<ActionLinkType | ActionFunctionType>;
}

export interface ActionLinkType extends ActionItemBase {
  type: "link" | "externalLink" | "edit";
  href: Url;
}

export interface ActionFunctionType extends ActionItemBase {
  type: "function" | "delete";
  function: (...args: unknown[]) => unknown;
}
export type ActionItem = ActionMenuType | ActionLinkType | ActionFunctionType;
